/**
 * 층 조명 제어 — 이 층(조명 위치)의 통로등·만공차등을 자동/수동 켬/수동 끔으로 바꾸고, 통로등은 평소·감지 시
 * 밝기를 정한다. 어드민 사이트의 층 조명 스위치·밝기 설정과 같은 조명 서비스 API 다. 수동은 재실·차량 감지가
 * 되돌리지 않으므로 점검이 끝나면 자동으로 돌려놔야 한다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import {
  QaLightChange,
  QaLightControl,
  QaLightMode,
  SiteParkingQaService,
} from '../../services/SiteParkingQaService';

type Kind = 'aisle' | 'parking';
type Choice = 'auto' | 'on' | 'off';

const KIND_LABEL: Record<Kind, string> = { aisle: '통로등', parking: '만공차등' };
const CHOICES: { value: Choice; label: string }[] = [
  { value: 'auto', label: '자동' },
  { value: 'on', label: '수동 켬' },
  { value: 'off', label: '수동 끔' },
];

/** 조명 서비스 모드 → 버튼. 모드가 비었으면(이 층에 그 조명 없음) 고른 버튼 없음. */
export const choiceOf = (m: QaLightMode): Choice | null =>
  m.mode === 'auto' ? 'auto' : m.mode === 'manual' && m.state ? m.state : null;
const modeOf = (c: Choice): QaLightMode =>
  c === 'auto' ? { mode: 'auto', state: '' } : { mode: 'manual', state: c };

/** 밝기 입력 검사 — 0 ≤ 평소 ≤ 감지 시 ≤ 100 정수. 맞으면 null. */
export function dimProblem(min: string, max: string): string | null {
  const lo = Number(min);
  const hi = Number(max);
  if (min.trim() === '' || max.trim() === '' || !Number.isInteger(lo) || !Number.isInteger(hi)) {
    return '밝기는 0~100 정수로 넣어 주세요';
  }
  if (lo < 0 || hi > 100 || lo > hi) return '0 ≤ 평소 ≤ 감지 시 ≤ 100 이어야 합니다';
  return null;
}

interface Props {
  projectId: number;
  ip: string;
  /** 조명 위치(P1_B1). */
  location: string;
  kind: Kind;
}

const QaLightControlPanel: React.FC<Props> = ({ projectId, ip, location, kind }) => {
  const [ctl, setCtl] = useState<QaLightControl | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dimMin, setDimMin] = useState('');
  const [dimMax, setDimMax] = useState('');

  const send = useCallback(
    async (change: QaLightChange = {}) => {
      setBusy(true);
      setError(null);
      try {
        const next = await SiteParkingQaService.lightControl(projectId, ip, location, change);
        setCtl(next);
        setDimMin(next.dimMin === undefined ? '' : String(next.dimMin));
        setDimMax(next.dimMax === undefined ? '' : String(next.dimMax));
      } catch (e) {
        setError(e instanceof Error ? e.message : '조명 제어 실패');
      } finally {
        setBusy(false);
      }
    },
    [projectId, ip, location],
  );

  useEffect(() => {
    send();
  }, [send]);

  const label = KIND_LABEL[kind];
  const current = ctl ? choiceOf(ctl[kind]) : null;
  const dimChanged =
    !!ctl && (dimMin !== String(ctl.dimMin ?? '') || dimMax !== String(ctl.dimMax ?? ''));
  const dimError = dimChanged ? dimProblem(dimMin, dimMax) : null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}
      data-testid={`qa-light-control-${kind}`}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 'auto' }}>
          {label} 제어 · {location}
        </Typography>
        <Button
          size="small"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
          onClick={() => send()}
          disabled={busy}
          sx={{ minHeight: 44 }}
        >
          {busy ? '적용 중…' : '다시 읽기'}
        </Button>
      </Stack>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={current}
        disabled={busy || !ctl}
        onChange={(_, v: Choice | null) => {
          if (v && v !== current) send({ [kind]: modeOf(v) });
        }}
        aria-label={`${label} 모드`}
        sx={{ mt: 1, '& .MuiToggleButton-root': { minHeight: 44, fontWeight: 700 } }}
      >
        {CHOICES.map((c) => (
          <ToggleButton key={c.value} value={c.value} data-testid={`qa-light-${kind}-${c.value}`}>
            {c.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {ctl && current === null && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          조명 서비스에 이 층 {label} 모드가 없습니다 — 층 조명 등록(초기화)을 확인하세요.
        </Typography>
      )}
      {current === 'on' || current === 'off' ? (
        <Alert severity="warning" sx={{ mt: 1 }} data-testid={`qa-light-${kind}-manual`}>
          수동 중 — 재실·차량 감지가 바꾸지 않습니다. 점검이 끝나면 자동으로 돌려놓으세요.
        </Alert>
      ) : null}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {kind === 'aisle' && ctl && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" fontWeight={700}>
            통로등 밝기
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            자동일 때 평소 밝기와 사람·차가 지나갈 때 밝기(%)입니다. 이 층 CCTV 전부에 적용됩니다.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
            {(
              [
                ['평소', dimMin, setDimMin, 'qa-dim-min'],
                ['감지 시', dimMax, setDimMax, 'qa-dim-max'],
              ] as const
            ).map(([name, value, set, testId]) => (
              <TextField
                key={name}
                label={`${name} %`}
                size="small"
                type="number"
                value={value}
                onChange={(e) => set(e.target.value)}
                disabled={busy}
                inputProps={{ min: 0, max: 100, inputMode: 'numeric', 'data-testid': testId }}
                sx={{ flex: 1, minWidth: 0, '& input': { fontSize: 16 } }}
              />
            ))}
            <Button
              variant="contained"
              disabled={busy || !dimChanged || !!dimError}
              onClick={() => send({ dimMin: Number(dimMin), dimMax: Number(dimMax) })}
              sx={{ minHeight: 44, flexShrink: 0 }}
              data-testid="qa-dim-apply"
            >
              밝기 적용
            </Button>
          </Stack>
          {dimError && (
            <Typography variant="caption" color="error" component="p" sx={{ mt: 0.5 }}>
              {dimError}
            </Typography>
          )}
          {ctl.dimMin === undefined && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
              조명 서비스에서 이 층 밝기를 읽지 못했습니다.
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default QaLightControlPanel;
